import React from "react"
import {
  Box,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react"
import QRCodePlaceholder from "components/elements/QRCodePlaceholder"
import { isDarkColor } from "utilities/colors"

export type GoogleWalletPassPreviewProps = {
  brandName: string
  /** The event name — the prominent title on a Google event ticket. */
  eventName: string
  /** Event date, e.g. "Jun 14". Comes from the event itself. */
  dateText?: string
  /** Event time, e.g. "7:00 PM". Comes from the event itself. */
  timeText?: string
  /** Venue. On the real pass this comes from the event itself. */
  venueText?: string
  /** Ticket holder — always the guest, shown natively on the card front. */
  holderName?: string
  /** Ticket field (auxiliary 3 on the builder), shown beside the holder. */
  ticketLabel?: string
  ticketText?: string
  /** Auxiliary 1 and 2, on the third and final card row Google allows. */
  auxiliaryRow?: { label: string; value: string }[]
  logoImageUrl?: string
  heroImageUrl?: string
  qrImageUrl?: string
  showQr?: boolean
  backgroundColor?: string
}

const PREVIEW_WIDTH = 320

const HEADER_HEIGHT_PX = 58
// Colored card body: venue, event name, date/time, the divider, ticket
// holder/ticket, and the QR all sit here, matching the real event-ticket
// front. The hero is the final full-width band.
const CONTENT_HEIGHT_PX = 398
// The optional Aux 1 / Aux 2 row: its top margin plus a label and value line.
const AUXILIARY_ROW_HEIGHT_PX = 48
// Google renders the hero full-width at its 1032x336 spec ratio.
const HERO_ASPECT_RATIO = 1032 / 336
const HERO_HEIGHT_PX = Math.round(PREVIEW_WIDTH / HERO_ASPECT_RATIO)

const FONT_FAMILY = "Roboto, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

export const GOOGLE_PASS_PREVIEW_WIDTH = PREVIEW_WIDTH
export const GOOGLE_PASS_FONT_FAMILY = FONT_FAMILY

/** Card height. The hero is an extra band at the very bottom when present. */
export const getGooglePassPreviewHeight = (heroImageUrl?: string, hasAuxiliaryRow = false) => (
  HEADER_HEIGHT_PX
  + CONTENT_HEIGHT_PX
  + (hasAuxiliaryRow ? AUXILIARY_ROW_HEIGHT_PX : 0)
  + (heroImageUrl ? HERO_HEIGHT_PX : 0)
)

/** Full-height front pass when a hero image is present. */
export const GOOGLE_PASS_PREVIEW_HEIGHT = getGooglePassPreviewHeight("hero")

// Fallback matches the API's WalletPass::Customization::DEFAULTS background.
const DEFAULT_BACKGROUND = "#EBECF1"

const CARD_BORDER = "#DADCE0"
const CARD_SHADOW = "0 2px 7px rgba(60, 64, 67, 0.16)"

const CARD_RADIUS = "20px"
const HEADER_PADDING_X = "18px"
const HEADER_GAP = "12px"
const LOGO_SIZE = "30px"
const BRAND_FONT_SIZE = "15px"
const BODY_PADDING_X = "16px"
const LABEL_FONT_SIZE = "11px"
const LABEL_LETTER_SPACING = "0.02em"
const TITLE_FONT_SIZE = "24px"
const TITLE_LINE_HEIGHT = "1.2"
const FIELD_VALUE_FONT_SIZE = "16px"
const VENUE_FONT_SIZE = "15px"
const LABEL_VALUE_GAP = "1px"
const QR_SIZE = "176px"
const QR_PADDING = "11px"
const QR_RADIUS = "14px"

const PassField = ({
  label,
  value,
  align,
  labelColor,
}: {
  label?: string
  value?: string
  align: "left" | "right"
  labelColor: string
}) => {
  if (!value) return null

  return (
    <Box minW={0} textAlign={align} flex={1}>
      {label && (
        <Text
          fontSize={LABEL_FONT_SIZE}
          lineHeight="1.2"
          fontWeight={500}
          color={labelColor}
          letterSpacing={LABEL_LETTER_SPACING}
          noOfLines={1}
        >
          {label}
        </Text>
      )}
      <Text
        mt={LABEL_VALUE_GAP}
        fontSize={FIELD_VALUE_FONT_SIZE}
        lineHeight="1.25"
        fontWeight={500}
        noOfLines={1}
      >
        {value}
      </Text>
    </Box>
  )
}

const GoogleWalletPassPreview = ({
  brandName,
  eventName,
  dateText,
  timeText,
  venueText,
  holderName,
  ticketLabel,
  ticketText,
  auxiliaryRow = [],
  logoImageUrl,
  heroImageUrl,
  qrImageUrl,
  showQr = true,
  backgroundColor = DEFAULT_BACKGROUND,
}: GoogleWalletPassPreviewProps) => {
  // Google derives text colors from the background's contrast, never from the builder's.
  const dark = isDarkColor(backgroundColor, 150)
  const textColor = dark ? "#FFFFFF" : "#202124"
  const labelColor = dark ? "rgba(255, 255, 255, 0.7)" : "#5F6368"
  const dividerColor = dark ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.1)"

  return (
    <Box
      w={`${PREVIEW_WIDTH}px`}
      h={`${getGooglePassPreviewHeight(heroImageUrl, auxiliaryRow.length > 0)}px`}
      bg={backgroundColor}
      border="1px solid"
      borderColor={CARD_BORDER}
      borderRadius={CARD_RADIUS}
      overflow="hidden"
      fontFamily={FONT_FAMILY}
      boxShadow={CARD_SHADOW}
      color={textColor}
      display="flex"
      flexDirection="column"
    >
      <Flex
        h={`${HEADER_HEIGHT_PX}px`}
        px={HEADER_PADDING_X}
        align="center"
        gap={logoImageUrl ? HEADER_GAP : 0}
        borderBottom="1px solid"
        borderColor={dividerColor}
        flexShrink={0}
      >
        {logoImageUrl && (
          <Box w={LOGO_SIZE} h={LOGO_SIZE} borderRadius="full" overflow="hidden" flexShrink={0}>
            <Image src={logoImageUrl} alt="Pass logo" w="100%" h="100%" objectFit="cover" draggable={false} />
          </Box>
        )}
        {brandName && (
          <Text fontSize={BRAND_FONT_SIZE} lineHeight="1.2" fontWeight={500} isTruncated>
            {brandName}
          </Text>
        )}
      </Flex>

      <Flex
        h={`${CONTENT_HEIGHT_PX + (auxiliaryRow.length > 0 ? AUXILIARY_ROW_HEIGHT_PX : 0)}px`}
        px={BODY_PADDING_X}
        pt="16px"
        pb="8px"
        direction="column"
        overflow="hidden"
        flexShrink={0}
      >
        {venueText && (
          <Text fontSize={VENUE_FONT_SIZE} lineHeight="1.35" fontWeight={400} noOfLines={1} mb="3px">
            {venueText}
          </Text>
        )}

        <Text
          fontSize={TITLE_FONT_SIZE}
          lineHeight={TITLE_LINE_HEIGHT}
          fontWeight={700}
          noOfLines={2}
        >
          {eventName}
        </Text>

        {/* Google localizes these labels per guest; shown in English here. */}
        {(dateText || timeText) && (
          <Flex mt="16px" justify="space-between" gap="16px">
            {dateText && (
              <PassField label="Date" value={dateText} align="left" labelColor={labelColor} />
            )}
            {timeText && (
              <PassField
                label="Time"
                value={timeText}
                align={dateText ? "right" : "left"}
                labelColor={labelColor}
              />
            )}
          </Flex>
        )}

        {(holderName || ticketText) && (
          <>
            <Box mt="18px" mb="18px" h="1px" bg={dividerColor} />
            <Flex justify="space-between" gap="16px">
              {holderName && (
                <PassField
                  label="Ticket Holder"
                  value={holderName}
                  align="left"
                  labelColor={labelColor}
                />
              )}
              {ticketText && (
                <PassField
                  label={ticketLabel || "Ticket"}
                  value={ticketText}
                  align={holderName ? "right" : "left"}
                  labelColor={labelColor}
                />
              )}
            </Flex>
          </>
        )}

        {auxiliaryRow.length > 0 && (
          <Flex mt="14px" justify="space-between" gap="16px">
            {auxiliaryRow.map((field, index) => (
              <PassField
                key={field.label || field.value}
                label={field.label}
                value={field.value}
                align={index > 0 ? "right" : "left"}
                labelColor={labelColor}
              />
            ))}
          </Flex>
        )}

        {showQr && (
          <Flex flex={1} align="center" justify="center" mt="16px">
            <Box w={QR_SIZE} h={QR_SIZE} p={QR_PADDING} bg="white" borderRadius={QR_RADIUS} overflow="hidden">
              {qrImageUrl ? (
                <Image src={qrImageUrl} alt="QR code" w="100%" h="100%" objectFit="contain" draggable={false} />
              ) : (
                <QRCodePlaceholder size="100%" text="Diobox Pass Builder" />
              )}
            </Box>
          </Flex>
        )}
      </Flex>

      {heroImageUrl && (
        <Box h={`${HERO_HEIGHT_PX}px`} overflow="hidden" flexShrink={0}>
          <Image src={heroImageUrl} alt="Pass hero" w="100%" h="100%" objectFit="cover" draggable={false} />
        </Box>
      )}
    </Box>
  )
}

export default GoogleWalletPassPreview
